import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { ReloadOutlined, SearchOutlined, SettingOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  TencentAdsRetentionAuditRow,
  TencentAdsRetentionConfigRow,
  TencentAdsRetentionMode,
  TencentAdsRetentionOverviewSummary,
  TencentAdsRetentionReportRow,
  TencentAdsRetentionRuntimeStatus,
  TencentAdsRetentionStatus,
  TencentAdsRetentionTrendRow,
} from "../../shared/types/TencentAdsRetentionManagement";
import { KpiStrip } from "./CommercialAnalyticsWidgets";
import { FilterBar, JsonBlock, Section, SectionHeader } from "./commercialAnalyticsStyles";
import { formatDateTime, formatInteger, formatPercent } from "./commercialAnalyticsUtils";
import {
  getTencentAdsRetentionOverview,
  getTencentAdsRetentionReports,
  retryTencentAdsRetentionReports,
  saveTencentAdsRetentionConfig,
} from "./tencentAdsRetentionAdminApi";

interface RetentionFilters {
  range: [Dayjs, Dayjs];
  statuses?: TencentAdsRetentionStatus[];
  cohortDate?: Dayjs;
  returnDate?: Dayjs;
  userId?: string;
  attributionChannel?: string;
  providerCode?: string;
}

interface RetentionOverviewData {
  config: TencentAdsRetentionConfigRow;
  runtime: TencentAdsRetentionRuntimeStatus;
  range: { startTime: number; endTime: number };
  summary: TencentAdsRetentionOverviewSummary;
  trends: TencentAdsRetentionTrendRow[];
  recentAudits: TencentAdsRetentionAuditRow[];
}

interface ConfigFormValues {
  mode: TencentAdsRetentionMode;
  paused: boolean;
  confirmed: boolean;
  reason: string;
  livePhrase?: string;
}

const STATUS_OPTIONS: Array<{ label: string; value: TencentAdsRetentionStatus }> = [
  { label: "等待激活", value: "waiting_activation" },
  { label: "仅采集", value: "captured" },
  { label: "待上报", value: "pending" },
  { label: "上报中", value: "reporting" },
  { label: "上报成功", value: "succeeded" },
  { label: "上报失败", value: "failed" },
  { label: "死信", value: "dead_letter" },
  { label: "已跳过", value: "skipped" },
];

const MODE_OPTIONS: Array<{ label: string; value: TencentAdsRetentionMode }> = [
  { label: "关闭（off）", value: "off" },
  { label: "仅采集（capture）", value: "capture" },
  { label: "正式回传（live）", value: "live" },
];

function createDefaultFilters(): RetentionFilters {
  return {
    range: [dayjs().subtract(7, "day").startOf("day"), dayjs().endOf("day")],
  };
}

export default function TencentAdsRetentionView() {
  const [configForm] = Form.useForm<ConfigFormValues>();
  const [draft, setDraft] = useState<RetentionFilters>(createDefaultFilters);
  const [filters, setFilters] = useState<RetentionFilters>(createDefaultFilters);
  const [overview, setOverview] = useState<RetentionOverviewData>();
  const [rows, setRows] = useState<TencentAdsRetentionReportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [accessibleTotal, setAccessibleTotal] = useState(0);
  const [maxAccessibleRecords, setMaxAccessibleRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [editingMode, setEditingMode] = useState<TencentAdsRetentionMode>("capture");
  const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
  const [detail, setDetail] = useState<TencentAdsRetentionReportRow>();
  const overviewSequence = useRef(0);
  const recordsSequence = useRef(0);

  const loadOverview = useCallback(async () => {
    const sequence = ++overviewSequence.current;
    setOverviewLoading(true);
    try {
      const data = await getTencentAdsRetentionOverview({
        startTime: filters.range[0].valueOf(),
        endTime: filters.range[1].valueOf(),
        auditLimit: 20,
      });
      if (sequence === overviewSequence.current) setOverview(data);
    } catch (error) {
      if (sequence === overviewSequence.current) {
        message.error(readErrorMessage(error, "次日留存回传概览加载失败"));
      }
    } finally {
      if (sequence === overviewSequence.current) setOverviewLoading(false);
    }
  }, [filters.range]);

  const loadReports = useCallback(async () => {
    const sequence = ++recordsSequence.current;
    setRecordsLoading(true);
    try {
      const data = await getTencentAdsRetentionReports({
        page,
        pageSize,
        startTime: filters.range[0].valueOf(),
        endTime: filters.range[1].valueOf(),
        statuses: filters.statuses,
        cohortDate: filters.cohortDate?.format("YYYY-MM-DD"),
        returnDate: filters.returnDate?.format("YYYY-MM-DD"),
        userId: normalizeOptionalFilter(filters.userId),
        attributionChannel: normalizeOptionalFilter(filters.attributionChannel),
        providerCode: normalizeOptionalFilter(filters.providerCode),
        sortOrder: "desc",
      });
      if (sequence !== recordsSequence.current) return;
      setRows(data.rows);
      setTotal(data.total);
      setAccessibleTotal(data.accessibleTotal);
      setMaxAccessibleRecords(data.maxAccessibleRecords);
      if (data.page !== page) setPage(data.page);
      const selectable = new Set(data.rows
        .filter((row) => row.retryable || row.promotableCaptured)
        .map((row) => row.id));
      setSelectedIds((current) => current.filter((id) => selectable.has(String(id))));
    } catch (error) {
      if (sequence === recordsSequence.current) {
        message.error(readErrorMessage(error, "次日留存回传明细加载失败"));
      }
    } finally {
      if (sequence === recordsSequence.current) setRecordsLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    void loadOverview();
    return () => { overviewSequence.current += 1; };
  }, [loadOverview]);

  useEffect(() => {
    void loadReports();
    return () => { recordsSequence.current += 1; };
  }, [loadReports]);

  const refreshAll = async () => {
    await Promise.all([loadOverview(), loadReports()]);
  };

  const applyFilters = () => {
    setSelectedIds([]);
    setPage(1);
    setFilters({ ...draft });
  };

  const resetFilters = () => {
    const next = createDefaultFilters();
    setDraft(next);
    setSelectedIds([]);
    setPage(1);
    setFilters(next);
  };

  const openConfigModal = () => {
    if (!overview?.config) return;
    const config = overview.config;
    setEditingMode(config.mode);
    configForm.setFieldsValue({
      mode: config.mode,
      paused: config.paused,
      confirmed: config.confirmed,
      reason: "",
      livePhrase: "",
    });
    setConfigModalOpen(true);
  };

  const submitConfig = async () => {
    let values: ConfigFormValues;
    try {
      values = await configForm.validateFields();
    } catch {
      return;
    }
    if (!overview?.config) {
      message.error("当前配置尚未加载，请刷新后重试");
      return;
    }
    if (values.mode === "live" && !values.confirmed) {
      message.error("正式回传前必须确认腾讯事件定义");
      return;
    }
    if (values.mode === "live" && values.livePhrase?.trim() !== "正式开启") {
      message.error("请输入“正式开启”完成强确认");
      return;
    }

    const persist = async () => {
      setSaving(true);
      try {
        const data = await saveTencentAdsRetentionConfig({
          mode: values.mode,
          paused: values.paused,
          confirmed: values.confirmed,
          apiVersion: "v3.0",
          timezone: "Asia/Shanghai",
          expectedRevision: overview.config.revision,
          reason: values.reason.trim(),
        });
        if (data.status === "revision_conflict") {
          Modal.warning({
            title: "配置已被其他管理员更新",
            content: `最新版本为 v${data.config.revision}，请重新加载后再修改。`,
          });
          await loadOverview();
          return;
        }
        message.success("次日留存运行配置已保存");
        setConfigModalOpen(false);
        await loadOverview();
      } catch (error) {
        message.error(readErrorMessage(error, "次日留存运行配置保存失败"));
      } finally {
        setSaving(false);
      }
    };

    if (values.mode === "live") {
      Modal.confirm({
        title: "最终确认开启腾讯正式回传？",
        content: values.paused
          ? "配置将切换为 live，但当前仍处于暂停状态；解除暂停后，符合门禁的任务会开始发送。"
          : "保存后，符合全部运行门禁的待上报任务会由游戏服发送至腾讯。该操作会写入审计日志。",
        okText: values.paused ? "确认保存 live 配置" : "确认正式开启",
        cancelText: "返回检查",
        okButtonProps: { danger: true },
        onOk: persist,
      });
      return;
    }
    await persist();
  };

  const executeRetry = async (reportIds: string[], promoteCaptured: boolean) => {
    setRetrying(true);
    try {
      const data = await retryTencentAdsRetentionReports({
        reportIds,
        promoteCaptured,
        reason: promoteCaptured ? "管理后台显式晋升 capture 任务" : "管理后台手动重试失败任务",
      });
      if (data.skippedCount) {
        message.warning(
          `已提交 ${data.retriedCount} 条，晋升 ${data.promotedCapturedCount} 条，跳过 ${data.skippedCount} 条`,
        );
      } else {
        message.success(
          promoteCaptured
            ? `已显式晋升 ${data.promotedCapturedCount} 条并提交处理`
            : `已提交 ${data.retriedCount} 条任务重试`,
        );
      }
      setSelectedIds([]);
      await refreshAll();
    } catch (error) {
      message.error(readErrorMessage(error, "次日留存任务重试提交失败"));
    } finally {
      setRetrying(false);
    }
  };

  const requestRetry = (reportIds: string[]) => {
    if (!reportIds.length) return;
    const selectedRows = rows.filter((row) => reportIds.includes(row.id));
    if (selectedRows.length !== reportIds.length) {
      message.warning("所选任务已不在当前页面，请刷新后重新选择");
      return;
    }
    const capturedCount = selectedRows.filter((row) => row.status === "captured").length;
    if (capturedCount > 0 && (reportIds.length !== 1 || capturedCount !== 1)) {
      message.warning("“仅采集”任务只能单独选择 1 条进行显式晋升，不能多选或与失败任务混选");
      return;
    }
    if (!capturedCount) {
      Modal.confirm({
        title: reportIds.length === 1 ? "确认重试这条任务？" : `确认重试 ${reportIds.length} 条任务？`,
        content: "服务端会再次校验任务状态、事件定义与幂等键；成功任务不会重复发送。",
        okText: "提交重试",
        cancelText: "取消",
        onOk: () => executeRetry(reportIds, false),
      });
      return;
    }

    Modal.confirm({
      title: `第一次确认：所选任务包含 ${capturedCount} 条“仅采集”记录`,
      content: "仅采集记录默认永不发送。继续操作表示你希望显式晋升这些记录，服务端仍会检查 live 门禁和事件定义。",
      okText: "继续确认",
      cancelText: "取消",
      okButtonProps: { danger: true },
      onOk: () => {
        Modal.confirm({
          title: "第二次确认：晋升并进入正式上报队列？",
          content: "晋升是有审计记录的显式操作。腾讯接收成功后无法从我方撤回，请确认选择范围无误。",
          okText: "确认晋升并提交",
          cancelText: "取消",
          okButtonProps: { danger: true },
          onOk: () => executeRetry(reportIds, true),
        });
      },
    });
  };

  const selectedReportIds = useMemo(() => selectedIds.map(String), [selectedIds]);
  const summary = overview?.summary;
  const abnormalCount = (summary?.failed || 0) + (summary?.deadLetter || 0);
  const processingCount = (summary?.pending || 0) + (summary?.reporting || 0);

  return (
    <>
      <Alert
        showIcon
        type="info"
        message="腾讯小游戏次日留存协议已锁定"
        description="接口版本 v3.0；行为 START_APP；length_of_stay = 1；自然日边界固定为 Asia/Shanghai。管理后台不会展示 access_token、微信 OpenID/UnionID 或原始 click_id。"
      />

      <RuntimePanel
        config={overview?.config}
        runtime={overview?.runtime}
        loading={overviewLoading}
        onEdit={openConfigModal}
      />

      <FilterBar>
        <DatePicker.RangePicker
          showTime
          allowClear={false}
          value={draft.range}
          style={{ width: 330 }}
          onChange={(value) => value && setDraft((current) => ({
            ...current,
            range: value as [Dayjs, Dayjs],
          }))}
        />
        <Select
          mode="multiple"
          allowClear
          maxTagCount="responsive"
          placeholder="上报状态"
          value={draft.statuses}
          options={STATUS_OPTIONS}
          style={{ width: 205 }}
          onChange={(statuses) => setDraft((current) => ({ ...current, statuses }))}
        />
        <DatePicker
          allowClear
          placeholder="D0 日期"
          value={draft.cohortDate}
          style={{ width: 132 }}
          onChange={(cohortDate) => setDraft((current) => ({ ...current, cohortDate: cohortDate || undefined }))}
        />
        <DatePicker
          allowClear
          placeholder="D1 日期"
          value={draft.returnDate}
          style={{ width: 132 }}
          onChange={(returnDate) => setDraft((current) => ({ ...current, returnDate: returnDate || undefined }))}
        />
        <Input
          allowClear
          placeholder="玩家 ID"
          value={draft.userId}
          style={{ width: 175 }}
          onChange={(event) => setDraft((current) => ({ ...current, userId: event.target.value }))}
        />
        <Input
          allowClear
          placeholder="归因渠道"
          value={draft.attributionChannel}
          style={{ width: 130 }}
          onChange={(event) => setDraft((current) => ({ ...current, attributionChannel: event.target.value }))}
        />
        <Input
          allowClear
          placeholder="腾讯状态码"
          value={draft.providerCode}
          style={{ width: 125 }}
          onChange={(event) => setDraft((current) => ({ ...current, providerCode: event.target.value }))}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={applyFilters}>查询</Button>
        <Button onClick={resetFilters}>重置</Button>
        <Button
          icon={<ReloadOutlined />}
          loading={overviewLoading || recordsLoading}
          onClick={() => void refreshAll()}
        >
          刷新
        </Button>
      </FilterBar>

      <KpiStrip items={[
        { label: "符合条件任务", value: formatInteger(summary?.total), hint: `含 click_id ${formatInteger(summary?.withClickId)} 条` },
        { label: "等待激活", value: formatInteger(summary?.waitingActivation), hint: `最早 ${formatDateTime(summary?.oldestWaitingAt)}` },
        { label: "仅采集", value: formatInteger(summary?.captured), hint: "默认不会自动发送" },
        { label: "处理中", value: formatInteger(processingCount), hint: `待上报 ${formatInteger(summary?.pending)} / 上报中 ${formatInteger(summary?.reporting)}` },
        { label: "上报成功", value: formatInteger(summary?.succeeded), hint: `成功率 ${formatPercent(summary?.successRate)}` },
        { label: "异常任务", value: formatInteger(abnormalCount), hint: `失败 ${formatInteger(summary?.failed)} / 死信 ${formatInteger(summary?.deadLetter)}` },
      ]} />

      <Section>
        <SectionHeader>
          <span>回传趋势</span>
          <Typography.Text type="secondary">按 D1 回访日期统计</Typography.Text>
        </SectionHeader>
        <Table<TencentAdsRetentionTrendRow>
          rowKey="date"
          size="small"
          loading={overviewLoading}
          dataSource={overview?.trends || []}
          pagination={false}
          scroll={{ x: 650, y: 280 }}
          columns={[
            { title: "D1 日期", dataIndex: "date", width: 130 },
            { title: "任务", dataIndex: "total", width: 100, render: formatInteger },
            { title: "成功", dataIndex: "succeeded", width: 100, render: formatInteger },
            { title: "失败", dataIndex: "failed", width: 100, render: formatInteger },
            { title: "死信", dataIndex: "deadLetter", width: 100, render: formatInteger },
            { title: "跳过", dataIndex: "skipped", width: 100, render: formatInteger },
          ]}
        />
      </Section>

      <Section>
        <SectionHeader>
          <Space wrap>
            <span>腾讯次日留存上报明细</span>
            <Typography.Text type="secondary">共 {formatInteger(total)} 条</Typography.Text>
            {total > accessibleTotal && (
              <Typography.Text type="warning">
                仅支持浏览前 {formatInteger(maxAccessibleRecords)} 条，请缩小筛选范围
              </Typography.Text>
            )}
          </Space>
          <Button
            type="primary"
            disabled={recordsLoading || !selectedReportIds.length}
            loading={retrying}
            onClick={() => requestRetry(selectedReportIds)}
          >
            重试/晋升{selectedReportIds.length ? `（${selectedReportIds.length}）` : ""}
          </Button>
        </SectionHeader>
        <Table<TencentAdsRetentionReportRow>
          rowKey="id"
          size="small"
          loading={recordsLoading}
          dataSource={rows}
          scroll={{ x: 1900 }}
          rowSelection={{
            selectedRowKeys: selectedIds,
            preserveSelectedRowKeys: false,
            onChange: setSelectedIds,
            getCheckboxProps: (row) => ({ disabled: !row.retryable && !row.promotableCaptured }),
          }}
          pagination={{
            current: page,
            pageSize,
            total: accessibleTotal,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (value) => `共 ${formatInteger(value)} 条`,
            onChange: (nextPage, nextPageSize) => {
              setSelectedIds([]);
              setPage(nextPageSize === pageSize ? nextPage : 1);
              setPageSize(nextPageSize);
            },
          }}
          columns={[
            { title: "创建时间", dataIndex: "createdAt", width: 170, render: formatDateTime },
            { title: "D0 日期", dataIndex: "cohortDate", width: 112, render: emptyText },
            { title: "D1 日期", dataIndex: "returnDate", width: 112, render: emptyText },
            { title: "D1 登录时间", dataIndex: "actionTime", width: 170, render: formatDateTime },
            { title: "玩家 ID", dataIndex: "userId", width: 180, ellipsis: true, render: copyableText },
            { title: "平台", dataIndex: "platform", width: 100, render: emptyText },
            {
              title: "归因",
              width: 145,
              render: (_, row) => (
                <Space size={4} wrap>
                  <span>{row.attributionChannel || "身份匹配"}</span>
                  {row.hasClickId && <Tag color="blue">click_id</Tag>}
                </Space>
              ),
            },
            {
              title: "微信身份",
              dataIndex: "hasWechatIdentity",
              width: 92,
              render: (value) => value ? <Tag color="success">已具备</Tag> : <Tag color="error">缺失</Tag>,
            },
            { title: "状态", dataIndex: "status", width: 105, render: renderStatus },
            { title: "尝试", dataIndex: "attempts", width: 70, render: formatInteger },
            { title: "腾讯状态码", dataIndex: "providerCode", width: 110, render: emptyText },
            {
              title: "结果信息",
              width: 230,
              ellipsis: true,
              render: (_, row) => {
                const text = row.providerMessage || row.lastError || row.skipReason || "-";
                return <Tooltip title={text}><span>{text}</span></Tooltip>;
              },
            },
            {
              title: "trace_id",
              dataIndex: "providerTraceId",
              width: 180,
              ellipsis: true,
              render: copyableText,
            },
            { title: "最近尝试", dataIndex: "lastAttemptAt", width: 170, render: formatDateTime },
            { title: "下次重试", dataIndex: "nextRetryAt", width: 170, render: formatDateTime },
            {
              title: "操作",
              fixed: "right",
              width: 130,
              render: (_, row) => (
                <Space size={0}>
                  <Button type="link" size="small" onClick={() => setDetail(row)}>详情</Button>
                  <Button
                    type="link"
                    size="small"
                    disabled={!row.retryable && !row.promotableCaptured}
                    onClick={() => requestRetry([row.id])}
                  >
                    {row.status === "captured" ? "晋升" : "重试"}
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Section>

      <Section>
        <SectionHeader>
          <span>最近管理操作</span>
          <Typography.Text type="secondary">所有配置变更和人工重试均保留审计记录</Typography.Text>
        </SectionHeader>
        <Table<TencentAdsRetentionAuditRow>
          rowKey="id"
          size="small"
          loading={overviewLoading}
          dataSource={overview?.recentAudits || []}
          pagination={false}
          scroll={{ x: 900, y: 260 }}
          columns={[
            { title: "时间", dataIndex: "createdAt", width: 170, render: formatDateTime },
            { title: "操作", dataIndex: "action", width: 130, render: renderAuditAction },
            { title: "操作人", dataIndex: "actorName", width: 130, render: emptyText },
            { title: "角色", dataIndex: "actorRole", width: 100, render: emptyText },
            { title: "原因", dataIndex: "reason", ellipsis: true, render: emptyText },
            {
              title: "结果",
              width: 180,
              render: (_, row) => row.action === "save_config"
                ? `v${row.beforeRevision ?? "-"} → v${row.afterRevision ?? "-"}`
                : `请求 ${formatInteger(row.requestedCount)} / 变更 ${formatInteger(row.changedCount)}`,
            },
          ]}
        />
      </Section>

      <Modal
        title="修改次日留存运行配置"
        open={configModalOpen}
        confirmLoading={saving}
        okText="保存配置"
        cancelText="取消"
        width={620}
        forceRender
        onCancel={() => setConfigModalOpen(false)}
        onOk={() => void submitConfig()}
      >
        <Alert
          showIcon
          type={editingMode === "live" ? "warning" : "info"}
          message="协议字段不可编辑"
          description="固定使用 v3.0、START_APP、length_of_stay = 1 和 Asia/Shanghai。模式与暂停状态由游戏服轮询后生效。"
          style={{ marginBottom: 16 }}
        />
        <Form<ConfigFormValues>
          form={configForm}
          layout="vertical"
          preserve={false}
          initialValues={{ mode: "capture", paused: false, confirmed: false }}
        >
          <Form.Item name="mode" label="运行模式" rules={[{ required: true }]}>
            <Select
              options={MODE_OPTIONS}
              onChange={(mode) => setEditingMode(mode)}
            />
          </Form.Item>
          <Form.Item name="paused" label="暂停发送" valuePropName="checked">
            <Switch checkedChildren="已暂停" unCheckedChildren="未暂停" />
          </Form.Item>
          <Form.Item name="confirmed" valuePropName="checked">
            <Checkbox>
              已确认腾讯事件定义为 START_APP，且 length_of_stay = 1
            </Checkbox>
          </Form.Item>
          <Form.Item
            name="reason"
            label="变更原因（审计必填）"
            rules={[
              { required: true, whitespace: true, message: "请填写本次配置变更原因" },
              { min: 4, message: "变更原因至少填写 4 个字符" },
              { max: 300, message: "变更原因不能超过 300 个字符" },
            ]}
          >
            <Input.TextArea rows={3} showCount maxLength={300} placeholder="说明切换模式、暂停或恢复发送的原因" />
          </Form.Item>
          {editingMode === "live" && (
            <Form.Item
              name="livePhrase"
              label="正式回传强确认"
              extra="请输入“正式开启”；保存前还会进行一次最终确认。"
              rules={[{ required: true, whitespace: true, message: "请输入“正式开启”" }]}
            >
              <Input autoComplete="off" placeholder="正式开启" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Drawer
        title="腾讯次日留存上报详情"
        width="min(720px, 94vw)"
        open={!!detail}
        destroyOnHidden
        onClose={() => setDetail(undefined)}
      >
        <Typography.Paragraph type="secondary">
          以下内容由管理服务端脱敏返回，不包含 access_token、微信 OpenID/UnionID 或原始 click_id。
        </Typography.Paragraph>
        <JsonBlock>{JSON.stringify(detail, null, 2)}</JsonBlock>
      </Drawer>
    </>
  );
}

function RuntimePanel({
  config,
  runtime,
  loading,
  onEdit,
}: {
  config?: TencentAdsRetentionConfigRow;
  runtime?: TencentAdsRetentionRuntimeStatus;
  loading: boolean;
  onEdit: () => void;
}) {
  const workerHealthy = runtime?.workerHealthy === true;
  const ready = runtime?.liveGate.ready === true;
  const blockedReasons = runtime?.liveGate.blockedReasons || [];
  return (
    <Section aria-busy={loading}>
      <SectionHeader>
        <span>运行配置与正式回传门禁</span>
        <Space wrap>
          <Tag color={modeColor(config?.mode)}>配置：{config?.mode || "unknown"}</Tag>
          <Tag color={modeColor(runtime?.runtimeMode)}>运行：{runtime?.runtimeMode || "unknown"}</Tag>
          <Tag color={workerHealthy ? "success" : "error"}>Worker：{workerHealthy ? "正常" : "异常"}</Tag>
          <Button icon={<SettingOutlined />} disabled={!config} onClick={onEdit}>修改配置</Button>
        </Space>
      </SectionHeader>

      <Alert
        showIcon
        type={ready ? "success" : "warning"}
        message={ready ? "正式回传门禁已全部通过" : "当前不会执行腾讯正式回传"}
        description={ready
          ? "游戏服处于 live 且未暂停，协议已确认、配置同步、事件定义和 Worker 均正常。"
          : blockedReasons.length
            ? blockedReasons.map(translateBlockedReason).join("；")
            : "运行状态尚未完整同步，请检查 Worker 心跳与配置版本。"}
        style={{ marginBottom: 12 }}
      />

      <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
        <Descriptions.Item label="API 版本">{config?.apiVersion || "v3.0"}</Descriptions.Item>
        <Descriptions.Item label="行为类型">{config?.actionType || "START_APP"}</Descriptions.Item>
        <Descriptions.Item label="留存天数">{config?.lengthOfStay ?? 1}</Descriptions.Item>
        <Descriptions.Item label="业务时区">{config?.timezone || "Asia/Shanghai"}</Descriptions.Item>
        <Descriptions.Item label="配置版本">v{config?.revision ?? "-"}</Descriptions.Item>
        <Descriptions.Item label="运行版本">v{runtime?.configRevision ?? "-"}</Descriptions.Item>
        <Descriptions.Item label="暂停状态">{config?.paused ? <Tag color="warning">已暂停</Tag> : <Tag color="success">未暂停</Tag>}</Descriptions.Item>
        <Descriptions.Item label="事件确认">{config?.confirmed ? <Tag color="success">已确认</Tag> : <Tag color="warning">未确认</Tag>}</Descriptions.Item>
        <Descriptions.Item label="配置同步"><GateTag passed={runtime?.configInSync} /></Descriptions.Item>
        <Descriptions.Item label="事件定义"><GateTag passed={runtime?.eventDefinitionHashAvailable} /></Descriptions.Item>
        <Descriptions.Item label="Worker 启用"><GateTag passed={runtime?.workerEnabled} /></Descriptions.Item>
        <Descriptions.Item label="live 门禁"><GateTag passed={ready} /></Descriptions.Item>
        <Descriptions.Item label="最近心跳">{formatDateTime(runtime?.workerHeartbeatAt)}</Descriptions.Item>
        <Descriptions.Item label="最近配置加载">{formatDateTime(runtime?.lastConfigReloadAt)}</Descriptions.Item>
        <Descriptions.Item label="最近尝试">{formatDateTime(runtime?.lastAttemptAt)}</Descriptions.Item>
        <Descriptions.Item label="最近成功">{formatDateTime(runtime?.lastSucceededAt)}</Descriptions.Item>
      </Descriptions>
      {runtime?.lastError && (
        <Typography.Paragraph type="danger" style={{ margin: "10px 0 0" }}>
          最近错误：{runtime.lastError}
        </Typography.Paragraph>
      )}
    </Section>
  );
}

function GateTag({ passed }: { passed?: boolean }) {
  if (passed === true) return <Tag color="success">通过</Tag>;
  if (passed === false) return <Tag color="error">未通过</Tag>;
  return <Tag color="warning">未知</Tag>;
}

function modeColor(mode?: string) {
  if (mode === "live") return "success";
  if (mode === "capture") return "processing";
  if (mode === "off") return "default";
  return "warning";
}

function translateBlockedReason(reason: string) {
  const map: Record<string, string> = {
    desired_live: "管理配置尚未切换为 live",
    desired_not_paused: "管理配置处于暂停状态",
    action_confirmed: "腾讯事件定义尚未确认",
    runtime_live: "游戏服运行模式尚未切换为 live",
    worker_enabled: "游戏服 Worker 未启用",
    config_in_sync: "管理配置与游戏服运行版本尚未同步",
    event_definition_ready: "事件定义尚未就绪",
  };
  return map[reason] || reason;
}

function renderStatus(value: string) {
  const map: Record<string, { text: string; color?: string }> = {
    waiting_activation: { text: "等待激活", color: "warning" },
    captured: { text: "仅采集", color: "cyan" },
    pending: { text: "待上报", color: "processing" },
    reporting: { text: "上报中", color: "blue" },
    succeeded: { text: "成功", color: "success" },
    failed: { text: "失败", color: "error" },
    dead_letter: { text: "死信", color: "magenta" },
    skipped: { text: "跳过" },
  };
  const item = map[value] || { text: value || "-" };
  return <Tag color={item.color}>{item.text}</Tag>;
}

function renderAuditAction(value: string) {
  if (value === "save_config") return <Tag color="blue">保存配置</Tag>;
  if (value === "retry_reports") return <Tag color="orange">人工重试</Tag>;
  return <Tag>{value || "-"}</Tag>;
}

function copyableText(value: unknown) {
  if (value == null || value === "") return "-";
  return <Typography.Text copyable={{ text: String(value) }}>{String(value)}</Typography.Text>;
}

function emptyText(value: unknown) {
  return value == null || value === "" ? "-" : String(value);
}

function normalizeOptionalFilter(value?: string) {
  return value?.trim() || undefined;
}

function readErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}
